import { useEffect, useRef, useState } from 'react'
import { StyledDynamicVideo, Image, VideoStyle } from './styles'
import useBBViewport from '@/hooks/useBBViewport'

export interface IDynamicVideoProps {
  posterImage: string
  title: string
  alt: string
  showMobileVideo?: boolean
  playOnce?: boolean
  // Only applies when playOnce is true.
  endFrame?: 'first' | 'last'
  videoSources: IDynamicVideoMediaSourcesFields[]
}
export type IDynamicVideoMediaSourcesFields = {
  source: string
  contentType: string
}

const DynamicVideo = ({
  posterImage,
  alt,
  showMobileVideo,
  playOnce = false,
  endFrame = 'last',
  videoSources,
}: IDynamicVideoProps) => {
  const [checkedMotionPreference, setCheckedMotionPreference] = useState<boolean>(false)
  const [isPlaying, setIsPlaying] = useState<boolean>(true)
  const videoRef = useRef<HTMLVideoElement>(null)
  const hasPlayedRef = useRef<boolean>(false)
  const viewport = useBBViewport()
  const isMobile = viewport === `xs` || viewport === 'sm' ? true : false
  const isShowVideoMobile = showMobileVideo || !isMobile
  useEffect(() => {
    if (!checkedMotionPreference) {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      if (prefersReducedMotion && isPlaying) {
        setIsPlaying(false)
      }
      setCheckedMotionPreference(true)
    }
  }, [checkedMotionPreference, isPlaying])

  useEffect(() => {
    if (!playOnce || !isPlaying) {
      return
    }
    const videoEl = videoRef.current
    if (!videoEl) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasPlayedRef.current) {
            hasPlayedRef.current = true
            videoEl.play().catch(() => undefined)
            observer.disconnect()
          }
        })
      },
      { threshold: 0.5 }
    )
    observer.observe(videoEl)

    return () => {
      observer.disconnect()
      hasPlayedRef.current = false
    }
  }, [playOnce, isPlaying, isShowVideoMobile])

  const handleEnded = () => {
    if (playOnce && endFrame === 'first' && videoRef.current) {
      videoRef.current.currentTime = 0
    }
  }

  return (
    <StyledDynamicVideo data-testid="DynamicVideo">
      {isShowVideoMobile ? (
        <video
          ref={videoRef}
          autoPlay={playOnce ? false : isPlaying}
          muted
          playsInline
          loop={!playOnce}
          preload={playOnce ? 'auto' : undefined}
          poster={posterImage}
          data-testid="dynamic-video"
          style={VideoStyle}
          onEnded={handleEnded}
        >
          {videoSources &&
            videoSources.length > 0 &&
            videoSources.map((el, index) => (
              <source key={`${el.source}_${index}`} src={el.source} type={el.contentType} />
            ))}
        </video>
      ) : (
        <Image id="videoImage" src={posterImage} alt={alt} />
      )}
    </StyledDynamicVideo>
  )
}

export default DynamicVideo
